import React, {useEffect, useState} from "react";
import {
  Grid,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  FormControl,
  FormHelperText,
  styled
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ApiDetails } from "../../dummyData";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
  overflow: 'visible'
}));

const RegistrationForm = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {

    console.log("registration");
    console.log("Props details:", props.details);
  }, [props.details]);

  const phoneRegExp = /^[2-9]{2}[0-9]{8}/;
  const passwordRegExp = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

  // Dynamic initial values that update when props.details changes
  const getInitialValues = () => ({
    first_name: props.details?.get("first_name") || "",
    last_name: props.details?.get("last_name") || "",
    email: props.details?.get("email_address") || "",
    phoneNumber: props.details?.get("phone_number") || "",
    password: "",
    confirmPassword: "",
  });

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().min(3, "It's too short").required("Required"),
    last_name: Yup.string().min(3, "It's too short").required("Required"),
    email: Yup.string().email("Enter valid email").required("Required"),
    phoneNumber: Yup.string()
        .matches(phoneRegExp, "Phone number has to start with 254")
        .required("Required"),
    password: Yup.string()
        .min(8, "Minimum characters should be 8")
        .matches(
            passwordRegExp,
            "Password must have one upper, lower case, number, special symbol"
        )
        .required("Required"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Password not matches")
        .required("Required"),
  });

  const onSubmit = (values, formikActions) => {
    setIsSubmitting(true);

    let formData = new FormData();
    console.log(values)
    console.log(props)

    formData.append(
        "details",
        JSON.stringify({
          first_name: values.first_name,
          last_name: values.last_name,
          phone_number: values.phoneNumber,
          password: values.password,
          email_address: values.email,
          landlord_id: props.details?.get("landlord_id") || "",
          inventory_checklist_template: "",
          tenant_contract_template: "",
          caretaker_contract_template: "",
        })
    );

    // API call
    fetch(ApiDetails + "pegasus/visionary/authorization/update", {
      method: "POST",
      headers: {
        // "Content-Type": "application/json",
        "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
      },
      body: formData,
    })
        .then(async (response) => {
          let data = await response.json();
          if (response.status === 200) {
            props.snackBar({
              text: "Details Updated Successfully",
              type: "success",
            });

            props.next();
          } else if (response.status === 401) {
            props.snackBar({
              text: "Maximum concurrent logins reached",
              type: "warning",
            });
          } else if (response.status === 400) {
            props.snackBar({
              text: "Invalid Credentials Provided.",
              type: "error",
            });
          } else if (response.status === 500) {
            if (data["Message"].includes("duplicate key")) {
              if (data["Message"].includes("owners_email_address_uindex")) {
                props.snackBar({
                  text: "Email Address Already Exists",
                  type: "error",
                });
                return;
              }
            }
            props.snackBar({
              text: "Something went wrong. Please try again later.",
              type: "error",
            });
          }

          setIsSubmitting(false);
          formikActions.setSubmitting(false);
        })
        .catch((err) => {
          console.log(err);
          props.snackBar({
            text: "Something Went Wrong. Try Again Later",
            type: "error",
          });

          setIsSubmitting(false);
          formikActions.setSubmitting(false);
        });
  };

  return (
      <Box sx={{ p: isMobile ? 2 : 3, maxWidth: '100%' }}>
        <StyledCard>
          <CardContent sx={{ p: isMobile ? 2 : 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h5" component="h1" fontWeight="bold" color="primary" gutterBottom>
                Complete Your Account Setup
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Provide your personal information to create your account
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                enableReinitialize={true}
            >
              {(formikProps) => (
                  <Form noValidate>
                    <Grid container spacing={3}>
                      {/* Personal Information Section */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
                          Personal Information
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name="first_name">
                          {({ field, meta }) => (
                              <FormControl fullWidth error={meta.touched && Boolean(meta.error)}>
                                <TextField
                                    {...field}
                                    label="First Name"
                                    variant="outlined"
                                    error={meta.touched && Boolean(meta.error)}
                                    helperText={meta.touched && meta.error}
                                    required
                                    fullWidth
                                    InputProps={{
                                      startAdornment: (
                                          <InputAdornment position="start">
                                            <PersonIcon color="primary" />
                                          </InputAdornment>
                                      ),
                                    }}
                                />
                              </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name="last_name">
                          {({ field, meta }) => (
                              <FormControl fullWidth error={meta.touched && Boolean(meta.error)}>
                                <TextField
                                    {...field}
                                    label="Last Name"
                                    variant="outlined"
                                    error={meta.touched && Boolean(meta.error)}
                                    helperText={meta.touched && meta.error}
                                    required
                                    fullWidth
                                    InputProps={{
                                      startAdornment: (
                                          <InputAdornment position="start">
                                            <PersonIcon color="primary" />
                                          </InputAdornment>
                                      ),
                                    }}
                                />
                              </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name="email">
                          {({ field, meta }) => (
                              <FormControl fullWidth error={meta.touched && Boolean(meta.error)}>
                                <TextField
                                    {...field}
                                    label="Email Address"
                                    variant="outlined"
                                    error={meta.touched && Boolean(meta.error)}
                                    helperText={meta.touched && meta.error}
                                    required
                                    fullWidth
                                    InputProps={{
                                      startAdornment: (
                                          <InputAdornment position="start">
                                            <EmailIcon color="primary" />
                                          </InputAdornment>
                                      ),
                                    }}
                                />
                              </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name="phoneNumber">
                          {({ field, meta }) => (
                              <FormControl fullWidth error={meta.touched && Boolean(meta.error)}>
                                <TextField
                                    {...field}
                                    label="Phone Number"
                                    variant="outlined"
                                    error={meta.touched && Boolean(meta.error)}
                                    helperText={meta.touched && meta.error}
                                    required
                                    fullWidth
                                    InputProps={{
                                      startAdornment: (
                                          <InputAdornment position="start">
                                            <PhoneIcon color="primary" />
                                          </InputAdornment>
                                      ),
                                    }}
                                />
                              </FormControl>
                          )}
                        </Field>
                      </Grid>

                      {/* Password Section */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2, mt: 2 }}>
                          Security
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name="password">
                          {({ field, meta }) => (
                              <FormControl fullWidth error={meta.touched && Boolean(meta.error)}>
                                <TextField
                                    {...field}
                                    type={showPassword ? "text" : "password"}
                                    label="Password"
                                    variant="outlined"
                                    error={meta.touched && Boolean(meta.error)}
                                    helperText={meta.touched && meta.error ? meta.error : "Minimum 8 characters with uppercase, lowercase, number & symbol"}
                                    required
                                    fullWidth
                                    InputProps={{
                                      startAdornment: (
                                          <InputAdornment position="start">
                                            <LockIcon color="primary" />
                                          </InputAdornment>
                                      ),
                                      endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                          </InputAdornment>
                                      ),
                                    }}
                                />
                              </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name="confirmPassword">
                          {({ field, meta }) => (
                              <FormControl fullWidth error={meta.touched && Boolean(meta.error)}>
                                <TextField
                                    {...field}
                                    type={showConfirmPassword ? "text" : "password"}
                                    label="Confirm Password"
                                    variant="outlined"
                                    error={meta.touched && Boolean(meta.error)}
                                    helperText={meta.touched && meta.error}
                                    required
                                    fullWidth
                                    InputProps={{
                                      startAdornment: (
                                          <InputAdornment position="start">
                                            <LockIcon color="primary" />
                                          </InputAdornment>
                                      ),
                                      endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                            >
                                              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                          </InputAdornment>
                                      ),
                                    }}
                                />
                              </FormControl>
                          )}
                        </Field>
                      </Grid>

                    </Grid>

                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                      <Alert severity="info" sx={{ mb: 3, maxWidth: '600px', mx: 'auto', borderRadius: 2 }}>
                        All fields marked with <strong>*</strong> are required. The document upload section is optional.
                      </Alert>

                      <LoadingButton
                          type="submit"
                          variant="contained"
                          size="large"
                          loading={isSubmitting}
                          loadingPosition="end"
                          endIcon={<CheckIcon />}
                          disabled={formikProps.isSubmitting}
                          sx={{
                            px: 4,
                            py: 1.2,
                            borderRadius: 2,
                            fontWeight: "bold"
                          }}
                      >
                        Continue
                      </LoadingButton>
                    </Box>
                  </Form>
              )}
            </Formik>
          </CardContent>
        </StyledCard>
      </Box>
  );
};

export default RegistrationForm;